/**
 * Common file type extensions.
 */
export const EXCEL_FILES = ['xls', 'xlsx']
export const EXCEL_TEMPLATE_FILES = ['xlt', 'xltm', 'xltx']
export const POWERPOINT_FILES = ['ppt', 'pptx']
export const POWERPOINT_TEMPLATE_FILES = ['pot', 'potm', 'potx']
export const WORD_FILES = ['doc', 'docx']
export const WORD_TEMPLATE_FILES = ['dot', 'dotm', 'dotx']
export const PDF_CONVERTIBLE_FILES = [
  'doc', 'docx', 'dot', 'dotx', 'dotm',
  'dsn', 'dwg',
  'eml', 'epub',
  'fluidframework', 'form',
  'htm', 'html',
  'loop', 'loot',
  'markdown', 'md',
  'msg', 'note',
  'odp', 'ods', 'odt',
  'page',
  'pps', 'ppsx', 'ppt', 'pptx',
  'pulse',
  'rtf',
  'task',
  'tif', 'tiff',
  'wbtx', 'whiteboard',
  'xls', 'xlsm', 'xlsx'
]
export const TEMPLATE_FILES = EXCEL_TEMPLATE_FILES
  .concat(WORD_TEMPLATE_FILES)
  .concat(POWERPOINT_TEMPLATE_FILES)
const IMAGE_FILES = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'svg']
const EMAIL_FILES = ['msg', 'eml']

/**
 * Convert string to Uint8Array.
 */
export function stringToUint (string) {
  const array = new Array(string.length)
  const chars = string.split('')
  chars.forEach((c, i) => {
    array[i] = c.charCodeAt(0)
  })
  return Uint8Array.from(array)
}

/**
 * Cleans a file name from invalid characters.
 * Also tidies up multiples of spaces with a single space.
 */
export function cleanFileName (fileName) {
  const original = String(fileName || '')
  // Normalize composed/decomposed Unicode variants so SharePoint treats overwrite targets consistently.
  let clean = original
    .normalize('NFC')
    .replace(/[<>:'"/\\|?*#%]/g, '')
    .split(' ')
    .filter(Boolean)
    .join(' ')

  if (original.endsWith(' ')) {
    clean += ' '
  }

  return clean
}

/**
 * Adds https:// to an url if it's missing (for hrefs)
 */
export function httpify (url) {
  const lower = url.toLowerCase()
  if (lower.startsWith('http://') || lower.startsWith('https://')) {
    return url
  } else {
    return `https://${url}`
  }
}

/**
 * Resolve the initial list context stored in document metadata.
 */
export function getSavedDocumentContext (properties) {
  const projectId = String(properties?.pdscol_project_number || '').trim()
  const contactId = String(properties?.pdscol_contact_number || '').trim()

  if (projectId) return { page: 'project', id: projectId }
  if (contactId) return { page: 'contact', id: contactId }
  return null
}

/**
 * Resolve template manager base URL with tenant-first precedence.
 */
export function getTemplateManagerBaseUrl (userData) {
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5001'
  }

  const tenantSettings = userData?.tenant_settings || {}
  console.info('resolving template manager url from tenant settings and user data', userData)
  const candidates = [
    tenantSettings?.tm?.url,
    tenantSettings?.template_manager_url,
    tenantSettings?.tm_url,
    tenantSettings?.template_manager_base_url,
    tenantSettings?.template_manager?.url,
    userData?.template_manager_url,
    userData?.tm_url,
    userData?.templateManagerUrl,
    process.env.REACT_APP_TM_URL
  ]

  for (const candidate of candidates) {
    const normalized = normalizeAbsoluteUrl(candidate)
    if (normalized.length > 0) {
      return normalized
    }
  }

  return ''
}

/**
 * Build a TM link with pds context query parameters.
 */
export function getTemplateManagerLink (baseUrl, item) {
  const normalizedBase = normalizeAbsoluteUrl(baseUrl)
  if (!normalizedBase.length) {
    return ''
  }

  const id = item?.id
  const type = item?.type
  if (id == null || typeof type !== 'string' || type.length === 0) {
    return normalizedBase
  }

  const hasQuery = normalizedBase.includes('?')
  const separator = hasQuery ? '&' : '/?'
  return `${normalizedBase}${separator}pds_id=${encodeURIComponent(String(id))}&pds_type=${encodeURIComponent(type)}`
}

function normalizeAbsoluteUrl (value) {
  const raw = String(value || '').trim()
  if (!raw.length) {
    return ''
  }

  const url = httpify(raw)
  try {
    return new URL(url).toString().replace(/\/$/, '')
  } catch {
    return ''
  }
}

/**
 * Compares two strings in lower case.
 */
function compareLowerCase (A, B) {
  const a = A.toLowerCase()
  const b = B.toLowerCase()
  if (a < b) return -1
  else if (a > b) return 1
  else return 0
}

/**
 * Sorts an array of folders and files.
 * Folders are sorted before files.
 * Folder and files are sorted between each other.
 */
export function sortFileItems (items) {
  return items.slice().sort((A, B) => {
    if (A.type === 'folder' && B.type === 'file') {
      return -1
    } else if (A.type === 'file' && B.type === 'folder') {
      return 1
    }
    return compareLowerCase(A.name, B.name)
  })
}

/**
 * Sorts an array of items based on their first character.
 * Special characters are sorted first, then numbers, and finally letters A-Ö.
 * @param {*} items
 * @returns {Array} Sorted array of items.
 */
export function sortArray (items) {
  // use slice to create a copy of the array before sorting, to avoid mutating the original array
  return items.slice().sort((a, b) => {
    const getGroup = str => {
      const firstChar = str.trim().charAt(0)
      if (/[^a-zA-Z0-9åäöÅÄÖ]/.test(firstChar)) return 0 // Special characters
      if (/[0-9]/.test(firstChar)) return 1 // Numbers
      return 2 // Letters A-Ö
    }

    const groupA = getGroup(a)
    const groupB = getGroup(b)

    if (groupA !== groupB) {
      return groupA - groupB
    }

    return a.localeCompare(b, 'sv', { sensitivity: 'base' }) // Use Swedish locale
  })
}

/**
 * Return true if a token has more than 90 seconds left
 */
export function isValidToken (token) {
  return typeof token === 'string' && getTokenExpiresInMs(token) > 90 * 1000
}

function getTokenExpiresInMs (token) {
  const { exp } = decodeToken(token)
  return exp * 1000 - Date.now()
}

/**
 * Decodes the payload from a json web token. Does not verify.
 */
export function decodeToken (token) {
  const parts = (token || '').split('.')

  if (parts.length !== 3) {
    return {}
  }

  let payload = {}

  try {
    payload = JSON.parse(atob(parts[1]))
  } catch (e) {
    console.error('Failed to parse encoded token payload')
  }

  return payload
}

/**
 * Return file extension from file name
 */
export function getFileExtension (name) {
  const split = name.split('.')
  return (split.length > 0 ? split[split.length - 1] : '').toLowerCase()
}

/**
 * Return true if file extension is an office zip file
 */
export function isOfficeZipFile (name) {
  return ['xlsx', 'docx', 'pptx'].includes(getFileExtension(name))
}

/**
 * Return office uri protocol based based on file extension.
 */
function getOfficeUriProtocol (ext) {
  if (WORD_FILES.includes(ext) ||
      WORD_TEMPLATE_FILES.includes(ext)) {
    return 'ms-word'
  } else if (POWERPOINT_FILES.includes(ext) ||
             POWERPOINT_TEMPLATE_FILES.includes(ext)) {
    return 'ms-powerpoint'
  } else if (EXCEL_FILES.includes(ext) ||
             EXCEL_TEMPLATE_FILES.includes(ext)) {
    return 'ms-excel'
  } else {
    return ''
  }
}

/**
 * Return true if extension is a template
 */
export function isTemplateFile (ext) {
  return TEMPLATE_FILES.includes(ext)
}

/**
 * Return an absolute url using host origin and a server relative url
 */
export function absoluteUrl (siteUrl, serverRelativeUrl) {
  const parsed = new URL(siteUrl)
  return encodeURI(`${parsed.origin}${serverRelativeUrl || ''}`)
}

/**
 * Converts keys and values from an object to a query string.
 */
export function toQueryString (params) {
  const query = Object.keys(params).map(key => {
    const value = params[key]
    if (typeof value === 'string') {
      return value ? `${key}=${encodeURI(value)}` : ''
    } else {
      return `${key}=${encodeURI(value)}`
    }
  }).filter(Boolean).join('&')
  return query ? `?${query}` : ''
}

/**
 * Replaces placeholders in template strings using %1, %2, etc.
 */
export function formatResourceString (template, ...values) {
  return values.reduce((result, value, index) => {
    return String(result).replace(`%${index + 1}`, String(value != null ? value : ''))
  }, String(template || ''))
}

/**
 * Open a document uri based on file type using the office uri scheme.
 * uri: full url to document or template
 * params: optional object which will passed as parameters to templates
 * options.target: optional target argument passed to window.open
 * options.useOfficeUri: if false for office files, open the raw uri directly
 * **NOTE** Currently disabling query parameters for opening templates
 * on Mac!
 */
export function openDocument (uri, params, options = {}) {
  const { target, useOfficeUri = true } = options
  const ext = getFileExtension(uri)
  const protocol = getOfficeUriProtocol(ext)
  if (protocol && useOfficeUri) {
    const isTemplate = isTemplateFile(ext)
    const command = isTemplate ? 'nft' : 'ofe'
    const query = isTemplate && params && isWindows() ? `${toQueryString(params)}` : ''
    const url = `${protocol}:${command}|u|${uri}${query}`
    console.info('opening document', url)
    window.open(url, target)
    return true
  } else if (protocol) {
    console.info('opening office document using direct uri', uri)
    window.open(uri, target)
    return true
  } else if (ext === 'pdf') {
    console.info('opening pdf document', uri)
    window.open(uri, target)
    return true
  } else if (IMAGE_FILES.includes(ext)) {
    console.info('opening image', uri)
    window.open(uri, target)
    return true
  } else if (EMAIL_FILES.includes(ext)) {
    console.info('opening email', uri)
    window.open(uri, target)
    return true
  } else {
    console.info('could not find a suitable application for', uri)
    return false
  }
}

/**
 * Creates a parameter object for use with openDocument()
 */
export function constructDocumentParams (userData, context) {
  return {
    // From userData
    pds_user: userData.name || '',
    pds_email: userData.email || '',
    pds_phone: userData.phone || '',
    pds_city: userData.city || '',
    pds_address: userData.address || '',
    pds_userhomesite: userData.homepage || '',
    pds_contact: userData.collection_name || '',
    pds_title: userData.object_type_name || '',
    pds_zipcode: userData.postal_code || '',
    // From context
    pds_type: (context && context.type) || '',
    pds_id: (context && context.id) || '',
    pds_eid: (context && context.eid) || ''
  }
}

/**
 * Returns true if there is a matching office protocol for the
 * file type on the url.
 */
export function canOpenDocument (uri) {
  if (typeof uri === 'string') {
    const ext = getFileExtension(uri)
    return !!(getOfficeUriProtocol(ext) || ext === 'pdf' || IMAGE_FILES.includes(ext)) || EMAIL_FILES.includes(ext)
  } else {
    return false
  }
}

/**
 * Function for figuring out if an item should be selected in a
 * tree view
 */
export function isSelected (selected) {
  return (item) => {
    if (selected) {
      return item.type === selected.type && item.id === selected.id
    } else {
      return false
    }
  }
}

/**
 * Categories are really document libraries under the parent department
 */
export function getItemUrl (item) {
  return item.type === 'category' ? item.collection_url : item.url
}

/**
 * Category document library uses category.id. All other items use 'files'
 * as their document library root.
 */
export function getItemRoot (item) {
  if (item.type === 'category') {
    return `files_${item.id}`
  } else if (item.type === 'templates') {
    return 'pdsMallar'
  } else {
    return 'files'
  }
}

/**
 * Waits delay milliseconds.
 * Returns a promise.
 */
export function wait (delay) {
  return new Promise(resolve => setTimeout(resolve, delay))
}

/**
 * Return true if this is a Windows machine.
 */
export function isWindows () {
  return navigator.platform.startsWith('Win')
}

/**
 * Return true if this is a Mac machine.
 */
export function isMac () {
  return navigator.platform.startsWith('Mac')
}
